import React, { useState, useEffect } from 'react';
import axios from 'axios';
import usePagination from '../../../utils/usePagination';
import Pagination from '../../../components/common/Pagination';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const EMPTY = { field_name: '', location: '', location_id: '', area_acres: '', ownership_type: 'owned', yearly_lease_amount: '', lease_start_date: '', lease_end_date: '', soil_type: '', status: 'active', notes: '' };

const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };

const LandTab = () => {
  const [lands, setLands] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchLands = () => {
    Promise.all([
      axios.get(`${API}/agriculture/lands`, { headers: headers() }),
      axios.get(`${API}/agriculture/settings/location`, { headers: headers() })
    ]).then(([landsRes, locRes]) => {
      setLands(landsRes.data.data);
      setLocations(locRes.data.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchLands(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (land) => {
    setForm({
      ...land,
      location_id: land.location_id || '',
      yearly_lease_amount: land.yearly_lease_amount || '',
      lease_start_date: land.lease_start_date?.split('T')[0] || '',
      lease_end_date: land.lease_end_date?.split('T')[0] || ''
    });
    setEditId(land.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.field_name || !form.area_acres) return alert('Field name and area are required');
    if (!form.location_id) return alert('Please select a location group');
    setSaving(true);
    try {
      if (editId) await axios.put(`${API}/agriculture/lands/${editId}`, form, { headers: headers() });
      else await axios.post(`${API}/agriculture/lands`, form, { headers: headers() });
      setShowForm(false); fetchLands();
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this land?')) return;
    await axios.delete(`${API}/agriculture/lands/${id}`, { headers: headers() });
    fetchLands();
  };

  // Live kanal preview while typing area
  const previewKanals = Math.round(parseFloat(form.area_acres || 0) * 8);

  // Map location_id -> group name for the table
  const locationNameById = (() => {
    const map = {};
    locations.forEach(l => { map[l.id] = l.value; });
    return map;
  })();

  const statusColor = { active: '#27ae60', fallow: '#f39c12', inactive: '#e74c3c' };
  const { paginatedData, currentPage, setCurrentPage, totalPages } = usePagination(lands, 10);
  const ownershipColor = { owned: '#3498db', rented: '#e67e22' };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Land Management</h2>
        <button onClick={openAdd} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>
          + Add Land
        </button>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total Fields', value: lands.length, color: '#3498db' },
          { label: 'Total Acres', value: lands.reduce((s, l) => s + parseFloat(l.area_acres || 0), 0).toFixed(1), color: '#27ae60' },
          { label: 'Total Kanals', value: lands.reduce((s, l) => s + parseInt(l.total_kanals || 0), 0), color: '#16a085' },
          { label: 'Active', value: lands.filter(l => l.status === 'active').length, color: '#2ecc71' },
          { label: 'Rented', value: lands.filter(l => l.ownership_type === 'rented').length, color: '#e67e22' },
        ].map(c => (
          <div key={c.label} style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${c.color}`, textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Field Name', 'Location', 'Area', 'Ownership', 'Yearly Lease', 'Soil Type', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lands.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No lands added yet. Click "+ Add Land" to start.</td></tr>
            ) : paginatedData.map((land, i) => (
              <tr key={land.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '14px 16px', fontWeight: '600', color: '#2c3e50' }}>{land.field_name}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>
                  {land.location_id && locationNameById[land.location_id]
                    ? <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#0F6E5620', color: '#0F6E56' }}>📍 {locationNameById[land.location_id]}</span>
                    : (land.location || '—')}
                </td>
                <td style={{ padding: '14px 16px', color: '#555' }}>
                  <div style={{ fontWeight: '600' }}>{parseFloat(land.area_acres || 0).toFixed(2)} acres</div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{land.total_kanals || 0} kanals</div>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: `${ownershipColor[land.ownership_type] || '#999'}20`, color: ownershipColor[land.ownership_type] || '#999' }}>
                    {land.ownership_type}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', color: '#555' }}>
                  {land.ownership_type === 'rented'
                    ? `Rs. ${parseFloat(land.yearly_lease_amount || 0).toLocaleString()}`
                    : '—'}
                </td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{land.soil_type || '—'}</td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: `${statusColor[land.status]}20`, color: statusColor[land.status] }}>
                    {land.status}
                  </span>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => openEdit(land)} style={{ padding: '6px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                    <button onClick={() => handleDelete(land.id)} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalRecords={lands.length}
          rowsPerPage={10}
        />
      </div>

      {/* Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>{editId ? 'Edit Land' : 'Add New Land'}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Field Name *</label>
                <input style={inputStyle} value={form.field_name} onChange={e => setForm({ ...form, field_name: e.target.value })} placeholder="e.g. North Field" />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Location (Group) *</label>
                <select style={inputStyle} value={form.location_id} onChange={e => setForm({ ...form, location_id: e.target.value })}>
                  <option value="">-- Select Location --</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.value}</option>)}
                </select>
                {locations.length === 0 && (
                  <div style={{ fontSize: '12px', color: '#e67e22', marginTop: '6px' }}>
                    No location groups yet. Add them in ⚙️ Settings → 📍 Locations.
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Area (Acres) *</label>
                <input style={inputStyle} type="number" step="0.01" value={form.area_acres} onChange={e => setForm({ ...form, area_acres: e.target.value })} placeholder="0.00" />
                {form.area_acres && (
                  <div style={{ fontSize: '12px', color: '#27ae60', marginTop: '6px', fontWeight: '600' }}>
                    = {previewKanals} kanals (auto-calculated)
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Ownership Type</label>
                <select style={inputStyle} value={form.ownership_type} onChange={e => setForm({ ...form, ownership_type: e.target.value })}>
                  <option value="owned">Owned</option>
                  <option value="rented">Rented</option>
                </select>
              </div>
              {form.ownership_type === 'rented' && <>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Yearly Lease Amount (Rs)</label>
                  <input style={inputStyle} type="number" value={form.yearly_lease_amount} onChange={e => setForm({ ...form, yearly_lease_amount: e.target.value })} placeholder="Total yearly lease for whole land" />
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '6px' }}>
                    This is the total annual lease for the whole land. It will be auto-split among crops by their kanal area.
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Lease Start Date</label>
                  <input style={inputStyle} type="date" value={form.lease_start_date} onChange={e => setForm({ ...form, lease_start_date: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Lease End Date</label>
                  <input style={inputStyle} type="date" value={form.lease_end_date} onChange={e => setForm({ ...form, lease_end_date: e.target.value })} />
                </div>
              </>}
              <div>
                <label style={labelStyle}>Soil Type</label>
                <input style={inputStyle} value={form.soil_type} onChange={e => setForm({ ...form, soil_type: e.target.value })} placeholder="e.g. Clay, Sandy, Loam" />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="fallow">Fallow</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : editId ? 'Update Land' : 'Add Land'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandTab;